import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import type { LyricTimingV1 } from 'fym-shared/src/music24/lyricTiming';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { hookLineRange, isMomentReady, LineRange, momentLengthOf, PickLine, rangeSpanSec } from '../fullVideoRules';
import { fullVideoMessages } from '../fullVideoMessages';
import { studioMessages } from '../studioMessages';
import { StudioDetailsProps } from './props';
import { DetailField } from './DetailField';
import { ChoiceChip, ChoiceChips } from './ChoiceChips';
import { MomentLinePicker } from './MomentLinePicker';
import { lyricsRowState } from './FullVideoLyricsRow';
import type { SongLyricsView } from '../../../../smartLinks/catalog/song/page/catalogSongPath';

const LENGTHS: ReadonlyArray<15 | 30> = [15, 30];

interface LyricMomentDetailsProps extends StudioDetailsProps {
  accountId: number;
  itemUuid: string;
  lines: readonly PickLine[];
  timing: LyricTimingV1 | null;
  isLoading: boolean;
  onOpenSongLyrics: (view?: SongLyricsView) => void;
}

/**
 * Step 3 of a lyric moment (design s7b): the lines (the hook lines are picked when the step opens)
 * and the length, 15 or 30 s from the span of the lines. While the lyrics sync, the list shows the
 * text with no times and "Make it" stays on (the job waits for the times).
 */
const LyricMomentDetailsComponent: React.FC<LyricMomentDetailsProps> = (props) => {
  const { accountId, itemUuid, details, song, onChange, lines, timing, isLoading, onOpenSongLyrics } = props;
  const intl = useIntl();
  const isSyncing = lyricsRowState(song) === 'syncing';
  const { lineFrom, lineTo } = details;
  const range = lineFrom === undefined || lineTo === undefined ? null : { lineFrom, lineTo };
  const lengthSec = details.lengthSec === 30 ? 30 : 15;
  const lengthTitle = intl.formatMessage(studioMessages.lengthTitle);
  const lengths = useMemo<Array<ChoiceChip<15 | 30>>>(
    () => LENGTHS.map((value) => ({ value, label: intl.formatMessage(studioMessages.seconds, { count: value }) })),
    [intl]
  );

  const select = useCallback(
    (next: LineRange) => onChange({ ...next, lengthSec: momentLengthOf(rangeSpanSec(lines, next)) }),
    [lines, onChange]
  );
  // True after the artist picks lines, or when the step opens with the lines of a version.
  const isOwnPick = useRef(lineFrom !== undefined);
  const pick = useCallback(
    (next: LineRange) => {
      isOwnPick.current = true;
      select(next);
    },
    [select]
  );
  // The hook lines are picked when the step opens, and again when the times come in, until the
  // artist picks lines.
  useEffect(() => {
    if (isOwnPick.current) {
      return;
    }
    const hook = hookLineRange(lines, song.hookStartSec);
    if (hook !== null && (hook.lineFrom !== lineFrom || hook.lineTo !== lineTo)) {
      select(hook);
    }
  }, [lineFrom, lineTo, lines, select, song.hookStartSec]);

  const isReady = isMomentReady({ isSyncing, timing, lineFrom, lineTo });

  return (
    <>
      <DetailField
        title={intl.formatMessage(fullVideoMessages.linesTitle)}
        hint={intl.formatMessage(fullVideoMessages.linesHint)}
      >
        {isLoading && <LoadingView />}
        {!isLoading && lines.length === 0 && (
          <span className="small text-muted">
            {intl.formatMessage(fullVideoMessages.linesEmpty)}{' '}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={() => onOpenSongLyrics('text')}
            >
              {intl.formatMessage(fullVideoMessages.addLyrics)}
            </button>
          </span>
        )}
        {lines.length > 0 && (
          <MomentLinePicker
            accountId={accountId}
            itemUuid={itemUuid}
            lines={lines}
            range={range}
            hookStartSec={song.hookStartSec}
            lengthSec={lengthSec}
            onPick={pick}
          />
        )}
        {isSyncing && <span className="small text-muted">{intl.formatMessage(fullVideoMessages.linesSyncing)}</span>}
        {!isSyncing && range !== null && !isReady && (
          <span className="small text-warning" data-testid="moment-not-timed">
            {intl.formatMessage(fullVideoMessages.linesNotTimed)}{' '}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => onOpenSongLyrics()}>
              {intl.formatMessage(fullVideoMessages.checkTiming)}
            </button>
          </span>
        )}
      </DetailField>
      <DetailField title={lengthTitle} hint={intl.formatMessage(fullVideoMessages.momentLengthHint)}>
        <ChoiceChips
          label={lengthTitle}
          options={lengths}
          value={lengthSec}
          onChange={(next) => onChange({ lengthSec: next })}
        />
      </DetailField>
    </>
  );
};

export const LyricMomentDetails = TypedMemo(LyricMomentDetailsComponent);
